"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { Lightbulb, TrendingUp, Users } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/features/jobs-deep-research";
// TODO: point to the Deep Research screen once its route is confirmed.
const APP = "/jobs";

// ============================================================================
// HERO TABS
// ============================================================================

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "company-snapshot",
    name: "Company Snapshot",
    title: "The Whole Company on One Screen",
    description:
      "Size, stage, funding, tech stack, leadership and the latest news. Know what the business does and where it is heading before you write a word.",
    imageSrcUrl: `${IMG}/hero-company-snapshot.png`,
    primaryActionText: "Research a Company",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "interview-process",
    name: "Interview Process",
    title: "Know Every Round Before It Starts",
    description:
      "See the usual rounds, how long the process takes and what each stage tends to cover, so you prepare for the interview you will actually face.",
    imageSrcUrl: `${IMG}/hero-interview-process.png`,
    primaryActionText: "Research a Company",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "salary-bands",
    name: "Salary Bands",
    title: "Negotiate With Real Numbers",
    description:
      "Pay ranges by level and city, with the median marked. Check an offer against the band and walk into the conversation knowing your number.",
    imageSrcUrl: `${IMG}/hero-salary-bands.png`,
    primaryActionText: "Research a Company",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
];

// ============================================================================
// FEATURE GROUP 1 (dark)
// ============================================================================

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "culture-signals",
    title: "Culture and Team Signals",
    subtitle: "WHAT IT IS LIKE TO WORK THERE",
    description:
      "Employee sentiment broken down by growth, engineering culture, pay, management and work-life balance, with real review excerpts on both sides.",
    benefits: [
      "Sentiment by category",
      "Pros and cons from reviews",
      "Team size and work mode",
      "Average tenure",
    ],
    primaryCTA: "Research a Company",
    primaryLink: APP,
    imageSrcUrl: `${IMG}/culture-signals.png`,
    icon: <Users className="w-4 h-4" />,
    stats: "Reviews summarised",
  },
  {
    id: "hiring-momentum",
    title: "Hiring Momentum",
    subtitle: "GROWING OR BACKFILLING?",
    description:
      "Open roles over time, hiring by team and the health signals that matter: revenue, funding runway, layoffs and leadership changes.",
    benefits: [
      "12-month open-roles trend",
      "Hiring by department",
      "Layoff and funding signals",
      "A plain-language verdict",
    ],
    primaryCTA: "Check a Company",
    primaryLink: APP,
    secondaryCTA: "Browse Jobs",
    secondaryLink: "/jobs",
    imageSrcUrl: `${IMG}/hiring-momentum.png`,
    icon: <TrendingUp className="w-4 h-4" />,
    stats: "Health signals included",
    orientation: "reversed",
  },
];

// ============================================================================
// FEATURE GROUP 2 (light)
// ============================================================================

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "talking-points",
    title: "An Interview Brief, Ready to Use",
    subtitle: "RESEARCH YOU CAN SAY OUT LOUD",
    description:
      "The research becomes three talking points that connect your experience to the company's current priorities, plus sharp questions to ask your interviewers.",
    benefits: [
      "Talking points tied to recent news",
      "Questions to ask the panel",
      "Words the company uses",
      "Download as a PDF",
    ],
    primaryCTA: "Get My Brief",
    primaryLink: APP,
    secondaryCTA: "Practise the Interview",
    secondaryLink: "/features/ai-mock-interview",
    imageSrcUrl: `${IMG}/talking-points.png`,
    icon: <Lightbulb className="w-4 h-4" />,
    stats: "Built for each role",
  },
];

// ============================================================================
// TESTIMONIALS (replace with real user quotes before publishing)
// ============================================================================

const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "The brief pointed out a product launch from that week. I opened my answer with it and the hiring manager spent five minutes telling me about the team behind it.",
    name: "Priya M.",
    role: "Marketing Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "The salary band showed my offer was below the median for the level. I asked for the midpoint with the data in hand and got it.",
    name: "Jalen Kyle",
    role: "Project Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "I almost applied to a company that had cut a third of its team. The hiring signals made it obvious, and I spent that week on better options.",
    name: "Sneha P.",
    role: "UX Researcher",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=800&fit=crop&q=80",
  },
];

// ============================================================================
// PAGE
// ============================================================================

export default function JobsDeepResearchPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "Know the Company.",
            titleHighlight: "Before You Apply.",
            subtitle:
              "One report covers the culture, the interview process, the pay bands and whether the company is really growing. Walk into every application and every interview already prepared.",
            ctaText: "Research a Company",
            ctaLink: APP,
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Research That Wins Interviews.",
            sectionSubtitle:
              "The questions candidates usually answer after joining, answered before you apply.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "Research Pays Off",
            subtitle: "From job seekers who walked in prepared",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About Jobs Deep Research",
            subtitle: "What is in a report and how to use it.",
            faqs,
            ctaTitle: "Ready to research your next company?",
            ctaText: "Research a Company",
            ctaLink: APP,
          },
        },
      ]}
    />
  );
}
